import { Component, computed, effect, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonContent, IonHeader, IonFooter } from '@ionic/angular/standalone';
import { HeaderComponent } from 'src/app/components/navigation/header/header.component';
import { FooterComponent } from 'src/app/components/navigation/footer/footer.component';
import { ModalController, LoadingController, ToastOptions } from '@ionic/angular/standalone';
import { SimpleButtonComponent } from 'src/app/components/primitives/simple-button/simple-button.component';
import { ButtonComponent } from 'src/app/components/primitives/button/button.component';
import { CounterComponent } from 'src/app/components/primitives/counter/counter.component';
import { StateService } from 'src/app/services/state.service';
import { ErrorService } from 'src/app/services/error.service';
import { CloudFunctionsService } from 'src/app/services/cloud.functions.service';
import { TLoadItemSingleResult } from '@chemist2u/types-client/C2U/Cloud/item';
import { toSignal } from '@angular/core/rxjs-interop';
import { ItemFullComponent } from 'src/app/components/view/item-full/item-full.component';
import { UiService } from 'src/app/services/ui.service';
import { AuthService } from 'src/app/services/auth.service';
import { CartService } from 'src/app/services/cart.service';
import { AmplitudeService } from 'src/app/services/amplitude.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-item',
    templateUrl: './item.page.html',
    styleUrls: ['./item.page.scss'],
    imports: [IonFooter, IonContent, IonHeader, CommonModule, FormsModule, HeaderComponent, FooterComponent, SimpleButtonComponent, ButtonComponent, CounterComponent, ItemFullComponent, SimpleButtonComponent]
})
export class ItemPage implements OnInit, OnDestroy {

  private stdError: ToastOptions = {
    message: "Item not found",
    header: "Error",
    position: "top",
    duration: 2000,
  }
  private $auth = inject(AuthService);
  private $ui = inject(UiService);
  private $cloud = inject(CloudFunctionsService);
  private $state = inject(StateService);
  private $cart = inject(CartService);
  private $error = inject(ErrorService);
  private modalController = inject(ModalController);
  private loadingController = inject(LoadingController);
  private $amplitude = inject(AmplitudeService);

  private user = toSignal(this.$state.bUser);
  private session = toSignal(this.$state.bSession);

  public isLoggedIn = this.$state.isLoggedIn;
  public hasAddress = this.$state.hasAddress;

  public swapEnabled = signal<boolean>(false);
  public count = signal<number>(1);
  public getTotalPrice: number = 0;

  private pharmacyUserId = signal<string | undefined>(undefined);

  @Input() modalItemId: string | undefined;
  public item = signal<TLoadItemSingleResult | undefined>(undefined);
  private itemID = signal<string | undefined>(undefined);

  private sessionSub?: Subscription;

  constructor() {
    effect(() => {
      this.getTotalPrice = this.price() * this.count();
    });
  }

  async ngOnInit() {
    try {
      this.setSessionDetails();
      this.setItemID();
      await this.loadItem();
      this.$amplitude.track('MODAL_SHOW_ITEM', { id: this.itemID()!, name: this.item()?.item.name! });
      this.$amplitude.track('ITEM_LOAD_SUCCESS');
      this.sessionWatch();
    } catch (error) {
      this.$amplitude.track('ITEM_LOAD_FAIL');
      this.$error.showToast({ ...this.stdError, message: "There was an error getting the product details." });
      this.dismiss();
    }
  }

  ngOnDestroy() {
    if (this.sessionSub) this.sessionSub.unsubscribe();
  }

  private sessionWatch() {
    this.sessionSub = this.$state.bSession.subscribe(async session => {
      const prevVal = this.pharmacyUserId();
      const nextVal = session?.pharmacy?.id;
      if (prevVal != nextVal) {
        this.setSessionDetails();
        await this.loadItem(); // If fails will dismiss
      }
    })
  }

  private async loadItem(pharmacy = this.pharmacyUserId()) {
    // Load item from the server
    const id = this.itemID();
    try {
      const item = await this.$cloud.loadItemSingle({ id, pharmacy });
      if (item == undefined) {
        throw new Error("Not found.");
      }
      this.item.set(item);
    } catch (error) {
      this.$error.showToast({ ...this.stdError, message: "There was an error getting the product details." });
      await this.dismiss();
    }
  }

  private setSessionDetails() {
    if (this.user() && this.session()) {
      this.pharmacyUserId.set(this.session()?.pharmacy?.id);
    }
  }

  setItemID() {
    try {
      if (this.modalItemId) {
        this.itemID.set(this.modalItemId);
      } else {
        throw new Error("Item not found");
      }
    } catch (e: any) {
      // Show error message and go back
      this.$error.showToast({ ...this.stdError, message: e.message });
      this.dismiss();
    }
  }

  public name = computed(() => {
    return this.item()?.item?.name || "Unamed";
  })

  public imageSrc = computed(() => {
    if (this.item()?.item?.featuredImage) {
      //@ts-ignore
      return `url(${this.item()?.item?.featuredImage?._url})`;
    }
    if (this.item()?.item?.featuredImageThumb) {
      //@ts-ignore
      return `url(${this.item()?.item?.featuredImageThumb?._url})`;
    }
    return null;
  });

  public price = computed(() => {
    return this.item()?.override?.price || this.item()?.item?.price || 0;
  });

  public description = computed(() => {
    return this.item()!.item!.description! || "";
  });


  public buttonLabel = computed(() => {
    return `Add ${this.count()} to cart`;
  });

  async dismiss(data?: any) {
    try {
      await this.modalController.dismiss(data);
      this.$amplitude.track('MODAL_DISMISS_ITEM');
    } catch (e) {
      await new Promise<void>(resolve => {
        setTimeout(async () => {
          await this.dismiss();
          resolve();
        });
      });
    }
  }

  async addToCart() {
    const loader = await this.loadingController.create();
    try {
      await loader.present();
      await this.$cart.addItem(this.item()!, this.count(), this.swapEnabled());
      this.$amplitude.track('ITEM_ADD_TO_CART_SUCCESS');
    } catch(e: any) {
      this.$amplitude.track('ITEM_ADD_TO_CART_FAIL');
      this.$error.showToast({ ...this.stdError, message: "Error adding item to cart." });
    } finally {
      await loader.dismiss();
      this.dismiss();
    }
  }

  async addAddress() {
    if (await this.modalController.getTop()) {
      await this.modalController.dismiss();
    }
    this.$ui.setAddress.next(undefined);
  }

  async logIn() {
    if (await this.modalController.getTop()) {
      await this.modalController.dismiss();
    }
    this.$auth.showLogin();
  }
}